import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import {
  ArrowRightToLine,
  CalendarDays,
  Circle,
  MessageSquareText,
  UsersRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ChannelUserAvatar from "@/components/pages/chats/component/chat-custom-components/ChannelUserAvatar";
import { isGroupChannel } from "@/components/pages/chats/utils/channel-mentions";
import { Button } from "@/components/ui/button";

interface InternalChatDetailsPanelProps {
  onCollapse: () => void;
  className?: string;
}

const formatDateTime = (value?: Date | string | null): string | null => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getDisplayName = (user?: { id?: string; name?: string } | null) =>
  user?.name || user?.id || "";

export const InternalChatDetailsPanel: React.FC<
  InternalChatDetailsPanelProps
> = ({ onCollapse, className }) => {
  const { t } = useTranslation();
  const { client } = useChatContext();
  const { channel, watcher_count } = useChannelStateContext();

  const members = useMemo(
    () =>
      Object.values(channel.state.members ?? {})
        .map((member) => member.user)
        .filter((user): user is NonNullable<typeof user> => Boolean(user?.id))
        .sort((first, second) =>
          getDisplayName(first).localeCompare(getDisplayName(second)),
        ),
    [channel.state.members],
  );

  const isGroup = isGroupChannel(channel);
  const otherMember = members.find((member) => member.id !== client.userID);
  const chatName = isGroup
    ? (channel.data?.name as string | undefined) || t("chats.groupChat")
    : getDisplayName(otherMember) ||
      (channel.data?.name as string | undefined) ||
      channel.data?.created_by?.name ||
      t("chats.internalChats");
  const memberCount = channel.data?.member_count ?? members.length;
  const createdAt = formatDateTime(channel.data?.created_at as Date | string);
  const lastActivityAt = formatDateTime(
    (channel.data?.last_message_at || channel.data?.updated_at) as
      | Date
      | string
      | null
      | undefined,
  );
  const createdBy = channel.data?.created_by?.name;

  return (
    <aside
      className={cn(
        "font-geist flex h-full min-h-0 flex-col gap-5 overflow-x-hidden overflow-y-auto bg-[#FBFAFF] py-4 pr-4 pl-2",
        className,
      )}
    >
      <div className="flex flex-row items-center gap-2">
        <Button
          type="button"
          variant="new_ghost"
          size="icon_sm"
          onClick={onCollapse}
          aria-label={t("chats.collapsePanel")}
          className="shrink-0 !bg-transparent text-neutral-600"
        >
          <ArrowRightToLine className="size-5" aria-hidden />
        </Button>
        <h2 className="text-eathy-comfort-1 text-lg font-semibold">
          {t("chats.chatDetails")}
        </h2>
      </div>

      <div className="border-base-border bg-secondary-badge flex flex-col items-center gap-3 rounded-xl border p-4 text-center">
        <ChannelUserAvatar
          name={chatName}
          isGroup={isGroup}
          className="size-14 rounded-2xl"
        />
        <h3 className="line-clamp-2 text-lg font-semibold text-black/96">
          {chatName}
        </h3>
        <span className="border-base-border rounded-full border bg-white px-3 py-1 text-xs font-medium text-black/65">
          {isGroup ? t("chats.groupChat") : t("chats.privateChat")}
        </span>
      </div>

      <div className="flex flex-col gap-3">
        <div className="border-base-border flex items-center gap-3 rounded-lg border p-3">
          <UsersRound className="size-4 text-black/55" />
          <div>
            <p className="text-xs text-black/55">{t("chats.members")}</p>
            <p className="text-sm font-medium text-black/96">{memberCount}</p>
          </div>
        </div>
        <div className="border-base-border flex items-center gap-3 rounded-lg border p-3">
          <Circle className="text-skyline-2 fill-skyline-2 size-4" />
          <div>
            <p className="text-xs text-black/55">{t("chats.online")}</p>
            <p className="text-sm font-medium text-black/96">
              {watcher_count ?? 0}
            </p>
          </div>
        </div>
        {createdAt ? (
          <div className="border-base-border flex items-center gap-3 rounded-lg border p-3">
            <CalendarDays className="size-4 text-black/55" />
            <div>
              <p className="text-xs text-black/55">{t("chats.createdAt")}</p>
              <p className="text-sm font-medium text-black/96">{createdAt}</p>
            </div>
          </div>
        ) : null}
        {lastActivityAt ? (
          <div className="border-base-border flex items-center gap-3 rounded-lg border p-3">
            <MessageSquareText className="size-4 text-black/55" />
            <div>
              <p className="text-xs text-black/55">{t("chats.lastActivity")}</p>
              <p className="text-sm font-medium text-black/96">
                {lastActivityAt}
              </p>
            </div>
          </div>
        ) : null}
        {createdBy ? (
          <div className="border-base-border rounded-lg border p-3">
            <p className="text-xs text-black/55">{t("chats.createdBy")}</p>
            <p className="text-sm font-medium text-black/96">{createdBy}</p>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-black/96">
          {t("chats.participants")}
        </h3>
        <div className="flex flex-col gap-2">
          {members.map((member) => (
            <div
              key={member.id}
              className="border-base-border flex items-center gap-3 rounded-lg border p-2"
            >
              <div className="relative">
                <ChannelUserAvatar name={getDisplayName(member)} />
                {member.online ? (
                  <span className="bg-skyline-2 absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border border-white" />
                ) : null}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-black/96">
                  {getDisplayName(member) || t("chats.unknownUser")}
                </p>
                {member.id === client.userID ? (
                  <p className="text-xs text-black/55">{t("chats.you")}</p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
};
